// Must match product_flat.name_norm materialization (parity with the pipeline's
// _name_norm in pipelines/product_flat_refresh/transform.py).
//   SQL: LOWER(REGEXP_REPLACE(REGEXP_REPLACE(NORMALIZE(x, NFD), r'\p{Mn}', ''), r"['’]", ''))
// Apostrophes (straight U+0027 and curly U+2019) are stripped — not replaced with a
// space — so possessive brands tokenize the way users type them: `Van Camp's` →
// `van camps` matches the query `van camps` (issue #183 Problem 1). LOG_ANALYZER treats
// the apostrophe as a delimiter, so without this strip `camp's` splits into `camp`/`s`.
export function normalizeQ(q: string): string {
  return q
    .normalize('NFD')
    .replace(/\p{Mn}/gu, '')
    .replace(/['’]/gu, '')
    .toLowerCase();
}

// Validation-time normalization: trim + NFC + reject control chars, cap 100 chars.
// Throws on invalid input; caller maps to 400.
export function cleanQ(raw: string): string {
  const s = raw.normalize('NFC').trim();
  if (s.length > 100) throw new Error('q too long (max 100)');
  // biome-ignore lint/suspicious/noControlCharactersInRegex: explicit CSRF control-char check
  if (/[\x00-\x1F\x7F]/.test(s)) throw new Error('q contains control chars');
  return s;
}

export const EAN13_RE = /^\d{13}$/;
// Jumbo/Santa Isabel: 6–20 digits. Unimarc: digits-UN (or other 2-letter tenant suffix).
export const SKU_JUMBO_SI_RE = /^\d{6,20}$/;
export const SKU_UNIMARC_RE = /^\d+-[A-Z]{2}$/;
export const UPC_A_RE = /^\d{12}$/;

export function isEan13(s: string): boolean {
  return EAN13_RE.test(s);
}

export function isSku(s: string): boolean {
  return SKU_JUMBO_SI_RE.test(s) || SKU_UNIMARC_RE.test(s);
}

export function isUpcA(s: string): boolean {
  return UPC_A_RE.test(s);
}

export function toEan13FromUpcA(s: string): string {
  return `0${s}`;
}

export function validateEan13Checksum(s: string): boolean {
  if (!EAN13_RE.test(s)) return false;
  let sum = 0;
  for (let i = 0; i < 12; i += 1) {
    const d = s.charCodeAt(i) - 48;
    sum += i % 2 === 0 ? d : d * 3;
  }
  const checksum = (10 - (sum % 10)) % 10;
  return checksum === s.charCodeAt(12) - 48;
}
